<template>
  <div class="mx-jersey-review">
    <!-- 头部占位符 -->
    <div id="header-placeholder"></div>

    <div v-if="isLoading" class="loading-overlay">
      <div class="loading-inner">
        <div class="spinner"></div>
        <p>Loading design...</p>
      </div>
    </div>

    <transition name="toast">
      <div v-if="toastMsg" :class="['toast', toastError ? 'toast-error' : 'toast-success']">
        {{ toastMsg }}
      </div>
    </transition>

    <div class="container">
      <div class="page-layout">
        <!-- 左侧：3D 只读预览 -->
        <div class="viewer-wrapper">
          <Viewer3D
            ref="viewer3d"
            :view-only="true"
            @model-loaded="onModelLoaded"
          />
        </div>

        <!-- 右侧面板 -->
        <div class="right-panel">
          <div class="panel-block comment-block">
            <Comments ref="comments" :current-garment="currentGarment" />
          </div>
          <div class="panel-block roster-block">
            <RosterManager ref="rosterManager" />
          </div>
        </div>
      </div>
    </div>

    <!-- 底部占位符 -->
    <div id="footer-placeholder"></div>

    <!-- 浮动联系按钮 -->
    <div class="floating-contact" id="floatingContact">
      <button class="contact-toggle" @click="toggleContactMenu" aria-label="Contact options" :aria-expanded="contactMenuOpen">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/>
        </svg>
      </button>
      <div class="contact-options" :class="{ 'open': contactMenuOpen }">
        <a href="https://wa.me/8613537631227" target="_blank" class="contact-option whatsapp">
          <i class="fab fa-whatsapp"></i>
          <span>WhatsApp</span>
        </a>
        <a href="mailto:68534150@QQ.com" class="contact-option email">
          <i class="fas fa-envelope"></i>
          <span>Email</span>
        </a>
        <a href="tel:+8613537631227" class="contact-option phone">
          <i class="fas fa-phone"></i>
          <span>Call</span>
        </a>
      </div>
    </div>
  </div>
</template>

<script>
import { useDesignStore } from '@/stores/designStore'
import { mapState } from 'pinia'
import Viewer3D from '@/components/Viewer3D.vue'
import Comments from '@/components/Comments.vue'
import RosterManager from '@/components/RosterManager.vue'


export default {
  name: 'mxjerseys',
  components: { Viewer3D, Comments, RosterManager },

  data() {
    return {
      isLoading: false,
      toastMsg: '',
      toastError: false,
      _toastTimer: null,
      contactMenuOpen: false,
    }
  },

  computed: {
    ...mapState(useDesignStore, ['currentGarment'])
  },

  mounted() {
    // ★ 第1步：最先执行 - 设置 store 中的款式
    const store = useDesignStore()
    store.currentGarment = 'Mx Jersey'
    
    // ★ 第2步：加载 header/footer
    this.loadHeaderFooter()
    
    // ★ 第3步：在 nextTick 中加载模型（确保 DOM 已渲染）
    this.$nextTick(() => {
        if (this.$refs.viewer3d) {
        console.log('Loading MX Jersey model...')
        this.$refs.viewer3d.loadGarment('Mx Jersey')
        }
    })
    
    // ★ 第4步：初始化数据
    this.init()
    this.setupOutsideClick()
    },

  methods: {
    loadHeaderFooter() {
      fetch('/header.html')
        .then(response => response.text())
        .then(data => {
          const header = document.getElementById('header-placeholder')
          if (header) header.innerHTML = data
        })
        .catch(error => console.error('Failed to load header:', error))
      fetch('/footer.html')
        .then(response => response.text())
        .then(data => {
          const footer = document.getElementById('footer-placeholder')
          if (footer) footer.innerHTML = data
        })
        .catch(error => console.error('Failed to load footer:', error))
    },

    async init() {
        // ★ 在开头再次确保款式正确
        const store = useDesignStore()
        store.currentGarment = 'Mx Jersey'
      const params = new URLSearchParams(window.location.search)
      const id = params.get('id')

      if (!id) {
        await this.$nextTick()
        if (this.$refs.viewer3d) this.$refs.viewer3d.loadGarment('Mx Jersey')
        return
      }

      this.isLoading = true
      try {
        const res = await fetch(`/load-design.php?id=${id}`)
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        const result = await res.json()

        if (!result.success || !result.all_garments) {
          this.showToast('Design not found', true)
          return
        }

        const store = useDesignStore()
        store.currentDesignId = result.design_id || id

        // 直接把 Mx Jersey 数据写入 store
        const garmentData = result.all_garments['Mx Jersey'] || {}

        // 恢复颜色
        store.userColors.clear()
        if (garmentData.colors) {
          for (const [part, colorHex] of Object.entries(garmentData.colors)) {
            const colorNum = typeof colorHex === 'string'
              ? parseInt(colorHex.replace('#', ''), 16)
              : colorHex
            store.userColors.set(part, colorNum)
          }
        }

        // 恢复图层
        store.partLayersMap.clear()
        if (garmentData.logos && garmentData.logos.length > 0) {
          for (const logo of garmentData.logos) {
            const layers = store.partLayersMap.get(logo.part) || []
            layers.push({
              id: logo.id, url: logo.url, isText: false,
              scale: logo.scale || 1, offsetX: logo.offsetX || 0,
              offsetY: logo.offsetY || 0, rotation: logo.rotation || 0,
              img: null
            })
            store.partLayersMap.set(logo.part, layers)
          }
        }
        if (garmentData.texts && garmentData.texts.length > 0) {
          for (const text of garmentData.texts) {
            const layers = store.partLayersMap.get(text.part) || []
            layers.push({
              id: text.id, isText: true,
              textContent: text.text, textFont: text.font,
              textColor: text.color, fontSize: text.fontSize || 80,
              scale: text.scale || 0.8, offsetX: text.offsetX || 0,
              offsetY: text.offsetY || 0, rotation: text.rotation || 0,
              img: null
            })
            store.partLayersMap.set(text.part, layers)
          }
        }
        store.currentGarment = 'Mx Jersey'

        // 触发模型加载
        await this.$nextTick()
        const viewer = this.$refs.viewer3d
        if (viewer) {
          if (viewer.isLoading) {
            await new Promise((resolve) => {
              const t = setInterval(() => {
                if (!viewer.isLoading && viewer.partsMap && viewer.partsMap.size > 0) {
                  clearInterval(t); resolve()
                }
              }, 100)
              setTimeout(() => { clearInterval(t); resolve() }, 8000)
            })
          } else if (!viewer.currentModel || viewer.partsMap.size === 0) {
            viewer.loadGarment('Mx Jersey')
            await new Promise((resolve) => {
              const t = setInterval(() => {
                if (viewer.currentModel && viewer.partsMap && viewer.partsMap.size > 0) {
                  clearInterval(t); resolve()
                }
              }, 100)
              setTimeout(() => { clearInterval(t); resolve() }, 8000)
            })
          }
        }

        // 合成纹理
        await this.compositeAllTextures()

        // 恢复 roster
        if (result.roster && result.roster.length > 0) {
          store.rosterList = result.roster.map(m => ({
            name: m.name || '',
            gender: m.gender || 'Male',
            style: 'Mx Jersey',
            size: m.size != null ? m.size : '',
            quantity: m.quantity != null && m.quantity !== '' ? m.quantity : '',
            message: m.message || ''
          }))
        } else {
          store.rosterList = []
        }
        await this.$nextTick()
        if (this.$refs.rosterManager?.loadFromStore) {
          this.$refs.rosterManager.loadFromStore()
        }

        // 加载评论
        if (this.$refs.comments?.loadComments) {
          this.$refs.comments.loadComments()
        }

      } catch (e) {
        console.error('Load error:', e)
        this.showToast('Failed to load design', true)
      } finally {
        this.isLoading = false
      }
    },

    onModelLoaded() {
      this.compositeAllTextures()
    },

    async compositeAllTextures() {
      const store = useDesignStore()
      const viewer = this.$refs.viewer3d
      if (!viewer || !viewer.partsMap || viewer.partsMap.size === 0) return

      if (typeof viewer.applyColorsToModel === 'function') {
        viewer.applyColorsToModel()
      }

      for (const [part, layers] of store.partLayersMap.entries()) {
        if (!layers || layers.length === 0) continue
        if (!viewer.partsMap.has(part)) continue

        for (const layer of layers) {
          if (!layer.isText && layer.url && !layer.img) {
            await new Promise((resolve) => {
              const img = new Image()
              img.crossOrigin = 'anonymous'
              img.onload = () => { layer.img = img; resolve() }
              img.onerror = () => resolve()
              img.src = layer.url
            })
          }
          if (layer.isText && layer.textContent && !layer.img) {
            const canvas = store._createTextCanvas(
              layer.textContent, layer.textFont || 'Arial',
              layer.textColor || '#000000', layer.fontSize || 80
            )
            const img = new Image()
            img.src = canvas.toDataURL()
            layer.img = img
          }
        }

        viewer.compositePartTexture(part, null, false)
      }
    },

    showToast(msg, isError = false) {
      this.toastMsg = msg
      this.toastError = isError
      if (this._toastTimer) clearTimeout(this._toastTimer)
      this._toastTimer = setTimeout(() => { this.toastMsg = '' }, 3000)
    },

    toggleContactMenu() {
      this.contactMenuOpen = !this.contactMenuOpen
    },

    setupOutsideClick() {
      document.addEventListener('click', (e) => {
        const container = document.getElementById('floatingContact')
        if (container && !container.contains(e.target)) {
          this.contactMenuOpen = false
        }
      })
    },
  },
}
</script>

<style scoped>
.mx-jersey-review {
  font-family: 'Inter', sans-serif;
  background: #ffffff;
  color: #333;
  min-height: 100vh;
}

.loading-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.65);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
}

.loading-inner {
  text-align: center;
  color: #fff;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid rgba(255, 255, 255, 0.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin: 0 auto 12px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.toast {
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  padding: 8px 20px;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 500;
  color: #fff;
  z-index: 10001;
  pointer-events: none;
}

.toast-success {
  background: #10b981;
}

.toast-error {
  background: #ef4444;
}

.toast-enter-active,
.toast-leave-active {
  transition: opacity 0.3s, transform 0.3s;
}

.toast-enter-from,
.toast-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(10px);
}

.container {
  width: 95%;
  max-width: 1400px;
  margin: 0 auto;
  padding: 1rem 0;
}

.page-layout {
  display: flex;
  gap: 2rem;
  flex-wrap: wrap;
  align-items: flex-start;
}

.viewer-wrapper {
  flex: 1;
  min-width: 280px;
}

.right-panel {
  flex: 1;
  min-width: 320px;
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
}

.panel-block {
  background: #f5f5f5;
  border-radius: 10px;
  padding: 1rem;
}

.comment-block {
  background: #eff6ff;
}

.roster-block {
  background: #f0fdf4;
}

.floating-contact {
  position: fixed;
  bottom: 80px;
  right: 20px;
  z-index: 1000;
}

.contact-toggle {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: #25d366;
  color: white;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(37, 211, 102, 0.4);
  transition: all 0.3s ease;
}

.contact-toggle:hover {
  transform: scale(1.1);
  box-shadow: 0 6px 20px rgba(37, 211, 102, 0.5);
}

.contact-options {
  position: absolute;
  bottom: 70px;
  right: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
  opacity: 0;
  visibility: hidden;
  transform: translateY(10px);
  transition: all 0.3s ease;
}

.contact-options.open {
  opacity: 1;
  visibility: visible;
  transform: translateY(0);
}

.contact-option {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: white;
  border-radius: 24px;
  text-decoration: none;
  color: #333;
  font-size: 0.85rem;
  font-weight: 500;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: all 0.2s;
  white-space: nowrap;
}

.contact-option:hover {
  transform: translateX(-4px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.contact-option.whatsapp {
  color: #25d366;
}

.contact-option.email {
  color: #ea4335;
}

.contact-option.phone {
  color: #4285f4;
}

@media (max-width: 900px) {
  .page-layout {
    flex-direction: column;
  }
  .viewer-wrapper,
  .right-panel {
    width: 100%;
    min-width: 0;
  }
}

@media (max-width: 850px) {
  .container {
    width: 100%;
    padding: 0.5rem;
  }
  .panel-block {
    padding: 0.6rem;
  }
  .floating-contact {
    bottom: 70px;
    right: 12px;
  }
  .contact-toggle {
    width: 48px;
    height: 48px;
  }
}
</style>